import React, { useCallback, useEffect, useMemo, useState } from "react";
import SettleModal from "./SettleModal";
import Pagination from "./Pagination";
import { getLoggedinUser } from "../../helpers/api_helper";
import {
  Card, CardBody, CardHeader, Col, Container, Row, Table, Button, Badge,
  Alert, Spinner, Nav, NavItem, NavLink, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import ResearchDisclaimer from "../../Components/Common/ResearchDisclaimer";
import {
  listResearch, getResearchFacets, setResearchVisibility, deleteResearch,
} from "../../services/userPredictionService";
import FilterBar from "./FilterBar";
import { ScorecardSummary } from "./Scorecard";
import DateFilter, { describeDateFilter } from "./DateFilter";

import RangePercent from "./RangePercent";
import { modelNameOf } from "../../helpers/modelName";
import { VerdictBadge } from "./VerdictBadge";
import ActualChange from "./ActualChange";
import ResultLegend from "./ResultLegend";
const SCOPES = [
  { id: "mine", label: "My research", hint: "Everything you generated, private and shared." },
  { id: "shared", label: "Shared by members", hint: "Research other members chose to make public." },
  { id: "all", label: "All visible", hint: "Yours plus everything shared publicly." },
];

const fmtMoney = (v) => (v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`);
const fmtDate = (v) => (v ? new Date(v).toLocaleString() : "—");


/** Outcome badge: pending until the scorer has run on the expired prediction. */
/**
 * The outcome, or a way to record it.
 *
 * A closed forecast is not "awaiting score" any more — nothing is coming to
 * score it. It is waiting for the member, so the cell is the button rather than
 * a badge next to one: this is the moment the record is either kept or quietly
 * abandoned, and a missed forecast is exactly the one nobody wants to come back
 * and enter.
 */
/**
 * How long a forecast has left, in the coarsest unit that is still true.
 *
 * "closes in 3 days" and "closes in 4 hours" are both actionable; a live
 * mm:ss counter is not, and would re-render the table every second to say
 * nothing new.
 */
const timeLeft = (until) => {
  const ms = new Date(until).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? "" : "s"}`;
  return `${Math.round(hours / 24)} days`;
};

const ResultCell = ({ row, canSettle, onSettle }) => {
  if (!row.is_scored) {
    const remaining = timeLeft(row.valid_until);
    if (remaining) {
      // What the reader wants from an open forecast is when to come back for
      // it. "Open" alone said only that it was not finished.
      return (
        <Badge color="light" className="text-body" title={`Closes ${fmtDate(row.valid_until)}`}>
          Closes in {remaining}
        </Badge>
      );
    }
    if (canSettle) {
      return (
        <Button size="sm" color="warning" className="fs-12 py-0 px-2"
                onClick={(e) => {
                  // The row navigates to the forecast. Without this the click
                  // opened the dialog and then immediately bubbled up, and the
                  // route change tore it down again — the button looked like it
                  // did nothing but go to the detail page.
                  //
                  // Stopped on the button rather than the cell, so the badges
                  // beside it still behave like the rest of the row.
                  e.stopPropagation();
                  onSettle(row);
                }}>
          Grade it
        </Button>
      );
    }
    return <Badge color="warning">Unsettled</Badge>;
  }
  return (
    <>
      <VerdictBadge verdict={row.member_verdict} />
      <span className="text-muted fs-12">
        {fmtMoney(row.actual_price)}
        {row.member_score !== null && row.member_score !== undefined
          ? ` · score ${row.member_score}` : ""}
      </span>
      <ActualChange row={row} />
    </>
  );
};

// Filter state lives in the URL. Local state would be lost the moment someone
// opened a prediction and pressed Back — which is the single most common way to
// use this page. In the query string the browser restores it for free, and the
// view can be linked to or bookmarked.
// "styles" is gone: members can only ask one way now, so filtering by prompt
// style offered a single option. Historical rows still carry the styles that
// existed when they were made, and the scorecard still separates them --
// this is about a filter that had nothing left to filter.
const LIST_FACETS = ["symbols", "timeframes", "models", "authors"];
const DATE_KEYS = ["createdFrom", "createdTo", "targetFrom", "targetTo"];

const readFiltersFromUrl = (params) => {
  const filters = {};
  LIST_FACETS.forEach((key) => {
    const raw = params.get(key);
    filters[key] = raw ? raw.split(",").filter(Boolean) : [];
  });
  return filters;
};

const readDatesFromUrl = (params) => {
  const dates = {};
  DATE_KEYS.forEach((key) => {
    const raw = params.get(key);
    if (raw) dates[key] = raw;
  });
  const preset = params.get("datePreset");
  if (preset) dates.preset = preset;
  return Object.keys(dates).length ? dates : null;
};

/**
 * Rows per page. Five models answer one prompt, so 25 is five whole questions
 * to a page and the boundary rarely cuts a comparison in half.
 */
const PAGE_SIZE = 25;

const MyResearchList = () => {
  document.title = "My Research | ForecastNest";

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const scope = searchParams.get("scope") || "mine";
  // Page 1 is the absence of the parameter, so the plain URL is the first page.
  const page = Math.max(parseInt(searchParams.get("page"), 10) || 1, 1);
  const filters = useMemo(() => readFiltersFromUrl(searchParams), [searchParams]);
  const dates = useMemo(() => readDatesFromUrl(searchParams), [searchParams]);

  /**
   * Write the next view into the URL.
   *
   * `replace` for filter changes so the back button leaves the page rather than
   * stepping back through every checkbox the member ticked on the way here.
   */
  const updateUrl = useCallback((changes) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      const empty = value == null || value === "" ||
        (Array.isArray(value) && value.length === 0);
      if (empty) next.delete(key);
      else next.set(key, Array.isArray(value) ? value.join(",") : value);
    });
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Anything that changes which rows match returns to page 1. Narrowing the
  // list while sitting on page 4 otherwise shows an empty table, which reads as
  // "no matches" rather than "you are past the end".
  const setScope = (next) => updateUrl({ scope: next === "mine" ? null : next, page: null });

  const setFilters = (next) =>
    updateUrl({
      ...LIST_FACETS.reduce((acc, key) => ({ ...acc, [key]: next[key] || [] }), {}),
      page: null,
    });

  const setDates = (next) => updateUrl({
    ...DATE_KEYS.reduce((acc, key) => ({ ...acc, [key]: (next || {})[key] || null }), {}),
    datePreset: (next || {}).preset || null,
    page: null,
  });

  const goToPage = (n) => {
    updateUrl({ page: n <= 1 ? null : n });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const [facets, setFacets] = useState(null);
  const [rows, setRows] = useState([]);
  // The whole filtered set, not this page. pendingSettlement in particular has
  // to be a server figure -- see the load below.
  const [pageInfo, setPageInfo] = useState({
    total: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 1, pendingSettlement: 0,
  });
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // The row awaiting delete confirmation. Deleting is not undoable, and the
  // rows all look alike at a glance, so it is worth naming what is going.
  const [pendingDelete, setPendingDelete] = useState(null);
  // The row being settled.
  const [pendingSettle, setPendingSettle] = useState(null);

  // Who is looking. A forecast is settleable by its owner; an admin may settle
  // anyone's, which is how a member who has gone quiet still gets a record.
  const me = getLoggedinUser();
  const myId = me?.id ?? me?.user?.id ?? null;
  const iAmAdmin = !!(me?.is_admin ?? me?.user?.is_admin);

  /**
   * Closed, ungraded, and mine to grade — one entry per question asked.
   *
   * Deduplicated by prompt_id because settling one answer settles every model
   * that answered the same question; counting rows would report one question
   * asked of five models as five things to do. Rows with no prompt_id are their
   * own group, which is what settlePrediction does with them too.
   */
  const readyToGrade = useMemo(() => {
    const now = Date.now();
    const seen = new Set();
    const out = [];
    for (const row of rows) {
      if (row.is_scored) continue;
      if (new Date(row.valid_until).getTime() > now) continue;
      if (!iAmAdmin && !(myId != null && Number(row.user_id) === Number(myId))) continue;
      const key = row.prompt_id ? `p${row.prompt_id}` : `r${row.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(row);
    }
    return out;
  }, [rows, iAmAdmin, myId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Only send facets that are actually set; the API treats absent as "all".
      const params = { scope };
      Object.entries(filters).forEach(([key, values]) => {
        if (values.length) params[key] = values.join(",");
      });
      DATE_KEYS.forEach((key) => {
        if (dates && dates[key]) params[key] = dates[key];
      });
      params.limit = PAGE_SIZE;
      if (page > 1) params.page = page;
      const res = await listResearch(params);
      setRows(res.data || []);
      setPageInfo({
        // Falling back to the page length keeps a not-yet-redeployed API
        // rendering one correct page rather than "0 forecasts".
        total: res.total ?? (res.data || []).length,
        page: res.page ?? 1,
        pageSize: res.pageSize ?? PAGE_SIZE,
        totalPages: res.totalPages ?? 1,
        pendingSettlement: res.pendingSettlement ?? 0,
      });
    } catch (e) {
      setFeedback({ type: "danger", text: "Could not load your research." });
    } finally {
      setLoading(false);
    }
  }, [scope, filters, dates, page]);

  useEffect(() => { load(); }, [load]);

  // The choices on offer depend on the scope, not on the current filter, so
  // narrowing to one model never hides the others from the dialog.
  useEffect(() => {
    (async () => {
      try {
        const res = await getResearchFacets({ scope });
        setFacets(res.data);
      } catch (e) {
        setFacets(null);
      }
    })();
  }, [scope]);

  // A value picked under one scope may not exist under another; drop stale ones
  // rather than leaving a filter that silently matches nothing.
  //
  // Reads the current filters directly instead of a functional update: they now
  // live in the URL, and there is no previous-state callback to hook into.
  useEffect(() => {
    if (!facets) return;
    const pruned = {};
    let changed = false;
    LIST_FACETS.forEach((key) => {
      const options = facets[key] || [];
      const current = filters[key] || [];
      // A facet the server does not report on cannot be pruned against — leaving
      // the selection alone is safer than silently clearing it.
      const kept = options.length
        ? current.filter((v) => options.some((o) => o.value === v))
        : current;
      pruned[key] = kept;
      if (kept.length !== current.length) changed = true;
    });
    if (changed) setFilters(pruned);
    // Deliberately keyed on facets alone. setFilters and filters are both derived
    // from searchParams, so listing them would re-run this on every URL change
    // and fight the very edit the member just made. Facets arriving is the only
    // event that can invalidate a selection.
  }, [facets]);

  const dateSummary = describeDateFilter(dates);
  const anyFilterActive =
    LIST_FACETS.some((key) => (filters[key] || []).length > 0) || !!dates;

  const clearEverything = () => updateUrl({
    ...LIST_FACETS.reduce((acc, key) => ({ ...acc, [key]: null }), {}),
    ...DATE_KEYS.reduce((acc, key) => ({ ...acc, [key]: null }), {}),
    datePreset: null,
  });

  const toggleShare = async (row) => {
    setBusyId(row.id);
    setFeedback(null);
    try {
      const res = await setResearchVisibility(row.id, row.is_private);
      setRows((prev) => prev.map((r) =>
        r.id === row.id ? { ...r, is_private: res.data.is_private } : r));
      setFeedback({
        type: "success",
        text: res.data.is_private
          ? `${row.symbol} research is private again.`
          : `${row.symbol} research is now shared publicly.`,
      });
    } catch (e) {
      setFeedback({ type: "danger", text: "Could not change the sharing setting." });
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    const row = pendingDelete;
    if (!row) return;
    setBusyId(row.id);
    setFeedback(null);
    try {
      await deleteResearch(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setPendingDelete(null);
      setFeedback({
        type: "success",
        text: `Deleted the ${row.symbol} ${row.timeframe} forecast recorded against ${modelNameOf(row)}.`,
      });
    } catch (e) {
      // The server explains a refusal — a scored forecast is part of the track
      // record — and that reason is more useful than a generic failure.
      setFeedback({
        type: "warning",
        text: e?.error || e?.message || "Could not delete that research.",
      });
      setPendingDelete(null);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="My Research" pageTitle="My Research" />
        <ResearchDisclaimer className="mb-3" />

        {feedback && (
          <Alert color={feedback.type} toggle={() => setFeedback(null)}>{feedback.text}</Alert>
        )}

        {/* Forecasts whose window has shut and which nobody has graded.
            The affordance for this used to be a small button in the Result
            column, several rows down a table -- easy to scroll past, and a
            forecast nobody settles is a forecast that never becomes a score,
            which is the only thing the record is for.

            Counted by prompt rather than by row: settling one answer settles
            every model that answered the same question, so "3 forecasts" when
            three models answered one question would overstate the work by
            three times. */}
        {/* The count is the server's, over the whole filtered set; the button
            can only act on a row this page actually holds.

            Those two used to be the same thing, and paging separated them. The
            list is newest-first and a forecast is only gradeable once its
            window has shut, so the work sorts to the LAST page -- the banner
            would have gone quiet on page 1 while forecasts sat ungraded out of
            sight. It now says how many there are wherever you are, and says
            plainly when none of them are here. */}
        {pageInfo.pendingSettlement > 0 && (
          <Card className="border border-warning mb-3">
            <CardBody className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div>
                <h5 className="mb-1">
                  {pageInfo.pendingSettlement === 1
                    ? "A forecast is ready to grade"
                    : `${pageInfo.pendingSettlement} forecasts are ready to grade`}
                </h5>
                <p className="text-muted fs-13 mb-0">
                  {readyToGrade.length === 0
                    ? "None of them are on this page — they are the oldest, so try the last page."
                    : readyToGrade.length === 1
                      ? `The window on ${readyToGrade[0].symbol} has closed.`
                      : `The windows on ${readyToGrade.map((r) => r.symbol).slice(0, 4).join(", ")}`
                        + `${readyToGrade.length > 4 ? " and others" : ""} have closed.`}
                    {" "}Enter the closing price to score what each model said.
                </p>
              </div>
              {readyToGrade.length > 0 ? (
                <Button color="warning" onClick={() => setPendingSettle(readyToGrade[0])}>
                  Grade {readyToGrade[0].symbol}
                </Button>
              ) : (
                <Button color="warning" outline
                        disabled={pageInfo.page >= pageInfo.totalPages}
                        onClick={() => goToPage(pageInfo.totalPages)}>
                  Go to the last page
                </Button>
              )}
            </CardBody>
          </Card>
        )}

        <Card>
          <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <Nav pills className="mb-0">
              {SCOPES.map((s) => (
                <NavItem key={s.id}>
                  <NavLink href="#" className={scope === s.id ? "active" : ""}
                           onClick={(e) => { e.preventDefault(); setScope(s.id); }}>
                    {s.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
            <Link to="/my-research/generate" className="btn btn-sm btn-primary">
              <i className="ri-add-line align-bottom me-1" />New research
            </Link>
          </CardHeader>
          <CardBody>
            <p className="text-muted fs-13">
              {SCOPES.find((s) => s.id === scope)?.hint}
            </p>

            {/* One line, and a way through to the full scorecard. The whole panel
                used to sit here and pushed the research itself off the screen. */}
            <ScorecardSummary scope={scope} />

            <FilterBar facets={facets} selected={filters} onChange={setFilters} loading={loading}
                       extraControls={<DateFilter value={dates} onChange={setDates} loading={loading} />}
                       extraChips={dateSummary && (
                         <Badge color="primary" className="d-inline-flex align-items-center">
                           <span className="opacity-75 me-1">Date:</span>
                           {dateSummary}
                           <button type="button" className="btn-close btn-close-white ms-2"
                                   style={{ fontSize: "0.5rem" }}
                                   aria-label="Remove date filter"
                                   onClick={() => setDates(null)} />
                         </Badge>
                       )} />

            {loading ? (
              <div className="text-center py-4"><Spinner /></div>
            ) : rows.length === 0 ? (
              <div className="text-center py-4">
                {anyFilterActive ? (
                  <>
                    <p className="text-muted mb-3">No research matches this filter.</p>
                    <Button color="light" onClick={clearEverything}>
                      Clear filters
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-muted mb-3">Nothing here yet.</p>
                    <Link to="/my-research/generate" className="btn btn-primary">
                      Generate your first research
                    </Link>
                  </>
                )}
              </div>
            ) : (
              <>
                <ResultLegend />
                <div className="table-responsive">
                  <Table className="align-middle table-nowrap mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Symbol</th>
                      <th>Horizon</th>
                      <th>Model</th>
                      <th>Created</th>
                      <th>Forecast range</th>
                      <th>Conf.</th>
                      <th>Target</th>
                      <th>Result</th>
                      <th>Visibility</th>
                      <th>By</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id} onClick={() => navigate(`/prediction/${row.id}`)}
                          style={{ cursor: "pointer" }}
                          title="Open prediction details">
                        <td className="fw-medium">{row.symbol}</td>
                        <td>{row.timeframe}</td>
                        <td>{modelNameOf(row)}</td>
                        <td>{fmtDate(row.input_time)}</td>
                        <td>
                          {fmtMoney(row.range_min)} – {fmtMoney(row.range_max)}
                          <RangePercent row={row} />
                        </td>
                        <td>{row.confidence ? `${Number(row.confidence).toFixed(0)}%` : "—"}</td>
                        <td>{fmtDate(row.valid_until)}</td>
                        <td>
                          <ResultCell
                            row={row}
                            canSettle={iAmAdmin || (myId != null && Number(row.user_id) === Number(myId))}
                            onSettle={setPendingSettle}
                          />
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {row.is_owner ? (
                            <Button size="sm" color={row.is_private ? "light" : "success"}
                                    disabled={busyId === row.id}
                                    onClick={() => toggleShare(row)}>
                              {busyId === row.id ? (
                                <Spinner size="sm" />
                              ) : row.is_private ? (
                                <><i className="ri-lock-line align-bottom me-1" />Private</>
                              ) : (
                                <><i className="ri-earth-line align-bottom me-1" />Shared</>
                              )}
                            </Button>
                          ) : (
                            <Badge color="success">Shared</Badge>
                          )}
                        </td>
                        <td>{row.author}</td>
                        <td onClick={(e) => e.stopPropagation()} className="text-end">
                          {row.is_owner && (
                            <Button
                              size="sm"
                              color="link"
                              className={row.is_scored ? "text-muted p-1" : "text-danger p-1"}
                              disabled={busyId === row.id || row.is_scored}
                              title={row.is_scored
                                ? "Scored forecasts are part of your track record and cannot be deleted"
                                : "Delete this research"}
                              onClick={() => setPendingDelete(row)}
                            >
                              <i className="ri-delete-bin-line align-bottom" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  </Table>
                </div>
              </>
            )}
          </CardBody>
        </Card>

        {!loading && (
          <Pagination
            page={pageInfo.page}
            totalPages={pageInfo.totalPages}
            total={pageInfo.total}
            pageSize={pageInfo.pageSize}
            onPage={goToPage}
            loading={loading}
          />
        )}

        <SettleModal
          isOpen={!!pendingSettle}
          toggle={() => setPendingSettle(null)}
          prediction={pendingSettle}
          // The other unsettled answers to the same prompt. They share a
          // baseline and a window, so they share an outcome and are settled
          // together — taken from rows already loaded rather than fetched again.
          siblings={pendingSettle?.prompt_id
            ? rows.filter((r) => r.prompt_id === pendingSettle.prompt_id
              && !r.is_scored
              && new Date(r.valid_until) <= new Date())
            : []}
          onSettled={(res) => {
            const n = res?.settled_count || 1;
            setFeedback({
              type: "success",
              text: n > 1 ? `${n} forecasts settled.` : "Settlement recorded.",
            });
            load();
          }}
        />

        <Modal isOpen={!!pendingDelete} toggle={() => setPendingDelete(null)} centered>
          <ModalHeader toggle={() => setPendingDelete(null)}>Delete this research?</ModalHeader>
          <ModalBody>
            {pendingDelete && (
              <>
                <p className="mb-2">
                  <strong>{pendingDelete.symbol}</strong> · {pendingDelete.timeframe} ·{" "}
                  {modelNameOf(pendingDelete)}
                </p>
                <p className="text-muted fs-13 mb-0">
                  This removes the forecast permanently. The prompt it was generated
                  from is kept, so you can paste the answer again against the right
                  model if you still have it.
                </p>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button color="danger" onClick={confirmDelete}
                    disabled={busyId === pendingDelete?.id}>
              {busyId === pendingDelete?.id ? <><Spinner size="sm" /> Deleting…</> : "Delete"}
            </Button>
          </ModalFooter>
        </Modal>

      </Container>
    </div>
  );
};

export default MyResearchList;
